/**
 * Public Audit Share Landing Page
 * Auditors land here from the email link. Branded preview + Download button.
 * No login required. Works for the full 30-day share validity.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Download, FileText, Calendar, Mail, ShieldCheck, AlertCircle, Loader2,
  Building2, User as UserIcon, CheckCircle2, Clock, Hash,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AuditShareLanding = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/documents/audit-shares/${shareId}/info`);
        if (!cancelled) {
          if (res.data?.success) setInfo(res.data);
          else setError('Paylaşım bilgisi alınamadı');
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 404) setError('Paylaşım bulunamadı');
          else setError(err.response?.data?.detail || 'Paylaşım bilgisi alınamadı');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  const handleDownload = () => {
    if (!info?.download_url) return;
    setDownloading(true);
    // Open in same tab — backend 302s to a fresh S3 URL
    window.location.href = info.download_url;
    setTimeout(() => setDownloading(false), 4000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-rose-50 p-4">
        <div data-testid="audit-share-error" className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Erişim Hatası</h2>
          <p className="text-sm text-gray-600">{error || 'Paylaşım bilgisi yüklenemedi'}</p>
          <p className="text-xs text-gray-400 mt-4">
            Lütfen e-postanızdaki linkin doğru olduğundan emin olun veya
            paylaşan kişiyle iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  if (!info.is_active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50 p-4">
        <div data-testid="audit-share-expired" className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Paylaşım Süresi Doldu</h2>
          <p className="text-sm text-gray-600">
            Bu paylaşım <b>{formatDate(info.expires_at)}</b> tarihinde sona erdi
            ya da iptal edildi.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Yeni bir paylaşım için lütfen paylaşan kişiyle iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audit-share-landing" className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
        {/* Brand header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-base font-bold text-gray-900">Advancecash.io</div>
              <div className="text-xs text-gray-500">Güvenli Denetim Paylaşımı</div>
            </div>
          </div>
          <a href={`mailto:${info.recipient_email}`}
             className="text-xs text-gray-500 hover:text-gray-700 hidden sm:inline-flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" /> {info.recipient_email}
          </a>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Top banner */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-7 text-white">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white/15 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <FileText className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-blue-100/90 mb-1">
                  Denetim Paylaşımı
                </div>
                <h1 className="text-xl sm:text-2xl font-bold truncate">
                  {info.share_label || 'Belge Paylaşımı'}
                </h1>
                <div className="text-sm text-blue-100/90 mt-1">
                  <span className="font-medium">{info.sharer_name}</span>
                  {info.company_name ? <span> · {info.company_name}</span> : null}
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                  <Hash className="w-3 h-3" /> Belge Sayısı
                </div>
                <div data-testid="audit-share-doc-count" className="text-xl font-bold text-gray-900">{info.doc_count}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                  <Download className="w-3 h-3" /> Boyut
                </div>
                <div className="text-xl font-bold text-gray-900">{formatBytes(info.size_bytes)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Paylaşım
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {info.created_at ? new Date(info.created_at).toLocaleDateString('tr-TR') : '—'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Geçerlilik
                </div>
                <div data-testid="audit-share-expires" className="text-sm font-semibold text-emerald-700">
                  {info.expires_at ? new Date(info.expires_at).toLocaleDateString('tr-TR') : '—'}
                </div>
              </div>
            </div>

            {/* Totals by currency */}
            {info.totals && Object.keys(info.totals).length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Toplam Tutarlar</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(info.totals).map(([cur, amt]) => (
                    <span key={cur} className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100">
                      {Number(amt).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cur}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recipient + Note */}
            {(info.recipient_name || info.note) && (
              <div className="rounded-lg bg-blue-50/40 border border-blue-100 p-4 space-y-2">
                {info.recipient_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <UserIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-600">Alıcı:</span>
                    <span className="font-medium text-gray-900">{info.recipient_name}</span>
                  </div>
                )}
                {info.note && (
                  <div className="text-sm">
                    <div className="text-xs text-gray-500 font-medium uppercase mb-1">Paylaşan kişinin notu</div>
                    <div className="text-gray-800 whitespace-pre-wrap">{info.note}</div>
                  </div>
                )}
              </div>
            )}

            {/* Contents description */}
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs font-medium text-gray-500 uppercase mb-2">ZIP İçeriği</div>
              <ul className="space-y-1 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> <b>KAPAK.pdf</b> — paylaşım özet sayfası
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> <b>belgeler_ozet.xlsx</b> — Excel belge listesi
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {info.doc_count} adet orijinal belge (YYYY-MM klasörlerinde)
                </li>
              </ul>
            </div>

            {/* CTA */}
            <button
              data-testid="audit-share-download-btn"
              onClick={handleDownload}
              disabled={downloading}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-md transition-all disabled:opacity-60"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Hazırlanıyor...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" /> ZIP Dosyasını İndir ({formatBytes(info.size_bytes)})
                </>
              )}
            </button>
            <p className="text-center text-xs text-gray-500">
              Bu paylaşım ile şu ana kadar <b>{info.download_count}</b> kez indirme yapıldı.
            </p>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <b className="text-gray-700">Güvenli paylaşım</b> — Bu link uzun, tahmin
                edilemez bir token içerir ve sadece size özeldir. Lütfen başkaları ile
                paylaşmayınız. Süresi dolduğunda dosyalara erişim mümkün olmayacaktır.
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-gray-400 mt-6">
          <Building2 className="w-3 h-3 inline mr-1" />
          Bu sayfa Advancecash.io üzerinden otomatik oluşturulmuştur.
        </div>
      </div>
    </div>
  );
};

export default AuditShareLanding;
